import { Separator } from '@components/ui/separator';
import { Heading } from '@components/ui/typography/heading';
import { Text } from '@components/ui/typography/text';
import { VStack, Flex } from '@components/ui/layout/flex';
import { ObfuscatedEmail } from '@components/ui/obfuscated-email';

export default function AccessibilityStatement() {
  return (
    <div className="animate-in fade-in duration-300">
      <VStack className="min-h-screen flex-col md:flex-row">
        <Flex className="w-full" align="start" justify="start">
          <div>
            <div>
              <Heading level={2}>Accessibility Statement</Heading>
              <Text className="text-lg pt-4 text-gray-500">
                Last Updated: May 4, 2026
              </Text>
            </div>
            <div className="pb-5">
              <Text className="pt-4 text-gray-800 text-lg font-medium">
                Our Accessibility Goal:
              </Text>
              <Text className="inline pb-8 pl-4 text-2xl font-semibold">
                Accessibility by Default.
              </Text>
            </div>

            <Text className="text-lg">
              Our vision is that all people using our software will find their needs met without
              changing a setting.
            </Text>
            <Text className="text-lg py-4">
              We aim to adhere as closely as possible to the Web Content Accessibility Guidelines
              2.1 (WCAG 2.1) from the World Wide Web Consortium (W3C), especially at the AA level.
              These guidelines show us how to make the site accessible to folks with different
              disabilities like blindness, motor issues, vision problems, cognitive challenges, and
              more. Following these guidelines helps us make sure our site works for everyone!
            </Text>
            <Text className="text-lg pb-4">
              If you have difficulty accessing any material on our pages or have suggestions on how
              we can improve, please email us at <ObfuscatedEmail user="support" />, and we will work with
              you to make the information available.
            </Text>

            <Separator />

            <div className="pb-5">
              <Text className="pt-4 text-gray-800 text-xl font-medium">
                This is what we&apos;re doing in service of our vision:
              </Text>
              <Text className="pl-4 py-2 text-lg">
                - We ensure adequate color contrast for increased legibility on all patient-facing
                web pages by automatically flagging when the selected colors do not provide
                sufficient contrast.
              </Text>
              <Text className="pl-4 py-2 text-lg">
                - All patient-facing webpages use a hyperlegible font developed by the Braille
                Institute of America, intended for optimal legibility by readers who are partially
                visually impaired.
              </Text>
              <Text className="pl-4 py-2 text-lg">
                - Date pickers and screen-readers often play poorly. That&apos;s why we prioritized
                creating a human-friendly date picker in addition to a natural language text input
                for folks who would prefer to type their date preference. Try typing
                &quot;tomorrow&quot;, &quot;June 2nd&quot;, &quot;in one year&quot; or
                &quot;2-12-2025&quot;.
              </Text>
              <Text className="pl-4 py-2 text-lg">
                - We strive to make all functionality available and navigable from your keyboard.
              </Text>
              <Text className="pl-4 py-2 text-lg">
                - We do not allow flashing or blinking animations or certain color combinations.
              </Text>
              <Text className="pl-4 py-2 text-lg">
                - We do not allow automatic audio to play from our websites.
              </Text>
              <Text className="pl-4 py-2 text-lg">
                - Images and photographs use Alternative Text &quot;ALT&quot; and/or
                &quot;TITLE&quot; attributes. ALT/TITLE attributes provide a written description of
                the image, which is accessible to screen readers and visible when the mouse is
                placed over the image. These attributes are also useful for people who have images
                turned off on their browser, in which case a description will display where the
                image used to be.
              </Text>
            </div>

            <Separator />

            <Text className="text-lg py-4">
              While we&apos;re making every effort to accommodate everyone&apos;s needs on the
              website, there could still be pages or sections that aren&apos;t fully accessible,
              are in the process of being made accessible, or lack adequate technological solutions
              for accessibility. However, we&apos;re continuously improving accessibility by
              adding, updating, and enhancing options and features and adopting emerging
              technologies that can help everyone navigate the internet with confidence.
            </Text>
            <Text className="text-lg pt-4">
              My ClinicAlly accepts no responsibility for the content or accessibility of the
              external websites or external documents linked to from our websites.
            </Text>
          </div>
        </Flex>
      </VStack>
    </div>
  );
}
